import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

interface ModelingCharacterProps {
    modelUrl: string;   // 외부에서 받을 모델 URL
    modelRef: React.RefObject<THREE.Group>;
    animationName?: string; // 외부에서 받을 애니메이션 이름, 기본값은 'Idle'
    onAnimationsLoaded?: (animations: string[]) => void;  // 애니메이션 목록을 외부로 전달
}

const ModelingCharacter: React.FC<ModelingCharacterProps> = ({ modelUrl, modelRef, animationName = 'Idle', onAnimationsLoaded }) => {
    const meshRef = useRef<THREE.Group>(null);
    const mixerRef = useRef<THREE.AnimationMixer | null>(null);
    const requestRef = useRef<number | null>(null);

    useEffect(() => {
        const loader = new GLTFLoader();

        const loadModel = async () => {
            try {
                // GLB 모델 로드
                const gltf = await loader.loadAsync(modelUrl);

                const avatarModel = gltf.scene;
                avatarModel.scale.set(2.5, 2.5, 2.5);
                avatarModel.position.set(-1, -3, -2.5);
                avatarModel.rotation.y = Math.PI / 2;
                avatarModel.traverse((child: any) => {
                    if (child.isMesh) {
                        const material = child.material;
                        material.transparent = true;
                        material.alphaTest = 0.5;
                        material.depthWrite = true;
                    }
                });

                if (meshRef.current) {
                    meshRef.current.add(avatarModel);
                }

                if (modelRef && modelRef.current) {
                    modelRef.current.add(avatarModel);  // 모델을 부모에서 참조 가능하게 설정
                }

                // 애니메이션 처리
                const mixer = new THREE.AnimationMixer(avatarModel);
                mixerRef.current = mixer;

                // 애니메이션 목록을 외부로 전달
                const animationClips = gltf.animations.map((clip: THREE.AnimationClip) => clip.name);
                if (onAnimationsLoaded) {
                    onAnimationsLoaded(animationClips.length > 0 ? animationClips : ['Idle']); // 애니메이션이 없으면 'Idle' 추가
                }

                const selectedAnimation = gltf.animations.find((clip: THREE.AnimationClip) => clip.name === animationName);
                if (selectedAnimation) {
                    const action = mixer.clipAction(selectedAnimation);
                    action.play();
                } else {
                    console.warn(`애니메이션 ${animationName}을(를) 찾을 수 없습니다.`);
                }

                const clock = new THREE.Clock();
                const animate = () => {
                    if (mixerRef.current) {
                        mixerRef.current.update(clock.getDelta());
                    }
                    requestRef.current = requestAnimationFrame(animate);
                };
                animate();
            } catch (error) {
                console.error('모델 로딩에 실패했습니다:', error);
            }
        };

        loadModel();

        return () => {
            if (requestRef.current) {
                cancelAnimationFrame(requestRef.current);
            }
            if (mixerRef.current) {
                mixerRef.current.stopAllAction();
            }
            if (meshRef.current) {
                meshRef.current.remove(meshRef.current.children[0]);
            }
        };
    }, [modelUrl, animationName]);

    return <group ref={meshRef} />;
};

export default ModelingCharacter;
